import { z } from 'zod';

/**
 * 現在の保存形式の版（semver）。
 *
 * - メジャー：互換性のない変更。`migrations` に変換を追加すること
 * - マイナー：後方互換な追加
 * - パッチ：説明・制約の修正
 *
 * @see docs/data-format/format-v1.md
 */
export const CURRENT_FORMAT_VERSION = '1.3.0';

const SEMVER_PATTERN = /^(0|[1-9]\d*)\.(0|[1-9]\d*)\.(0|[1-9]\d*)$/;

/** `MAJOR.MINOR.PATCH` 形式の文字列（プレリリース・ビルドメタデータは扱わない）。 */
export const semverSchema = z
  .string()
  .regex(SEMVER_PATTERN, { message: 'MAJOR.MINOR.PATCH 形式の版を指定してください' });

/** すべてのトップレベル文書が持つ版情報。 */
export const versionHeaderSchema = z.object({
  formatVersion: semverSchema,
  applicationVersion: semverSchema,
  physicsEngineVersion: semverSchema,
});

/** 版情報。 */
export type VersionHeader = z.infer<typeof versionHeaderSchema>;

/** 分解した semver。 */
export interface SemVer {
  readonly major: number;
  readonly minor: number;
  readonly patch: number;
}

/**
 * `MAJOR.MINOR.PATCH` を分解する。
 *
 * @throws {RangeError} 形式が正しくない場合
 */
export function parseSemver(version: string): SemVer {
  const match = SEMVER_PATTERN.exec(version);
  if (!match) {
    throw new RangeError(`版の形式が正しくありません: ${version}`);
  }
  const [, major, minor, patch] = match;
  return { major: Number(major), minor: Number(minor), patch: Number(patch) };
}

/**
 * 読み込むデータの版と現在の版の関係。
 *
 * - `current`：同じメジャー版。そのまま検証して読み込める（新しいマイナー版の追加項目は除去される）
 * - `needs-migration`：古いメジャー版。マイグレーションが必要
 * - `unsupported-newer`：新しいメジャー版。読み込まずアプリの更新を促す
 */
export type FormatCompatibility = 'current' | 'needs-migration' | 'unsupported-newer';

/** データの `formatVersion` が現在のアプリで扱えるかを判定する。 */
export function checkFormatCompatibility(
  formatVersion: string,
  currentVersion: string = CURRENT_FORMAT_VERSION,
): FormatCompatibility {
  const data = parseSemver(formatVersion);
  const current = parseSemver(currentVersion);
  if (data.major < current.major) return 'needs-migration';
  if (data.major > current.major) return 'unsupported-newer';
  return 'current';
}
